import { enumPresentation } from "@/lib/enum";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { actingPrincipalText } from "@/components/ui/ActingPrincipal/text";
import { SkeletonTable } from "@/components/ui/Skeleton";
import { Text } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import type { AuthzChallenge } from "@gram/client/models/components/authzchallenge.js";
import type { ChallengeBucket } from "@gram/client/models/components/challengebucket.js";
import { Outcome } from "@gram/client/models/operations/listchallengebuckets.js";
import { useChallengeBuckets } from "@gram/client/react-query/challengeBuckets.js";
import { useChallenges } from "@gram/client/react-query/challenges.js";
import { useReadableAgents } from "@/hooks/useReadableAgents";
import { Badge as MoonshineBadge } from "@/components/ui/Badge";
import { type Column, Table } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { Check, Loader2 } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { isDisplayableBucket, principalDisplayName } from "./challengeHelpers";
import { useChallengeRowColumns } from "./useChallengeRowColumns";
import { useGrantFlow } from "./useGrantFlow";

type BucketOutcome = ChallengeBucket["outcome"];
type OutcomeFilter = "all" | "deny" | "allow" | "resolved";

/**
 * What the trigger says for the selected principal. `label` is the option's
 * own wording, which for an agent is its name — the raw value is its urn.
 */
function principalFilterLabel(value: string, label?: string): string {
  switch (value) {
    case "all":
      return "Everyone";
    case "user:all":
      return "All users";
    default:
      return label ?? value;
  }
}

/** Map an individual AuthzChallenge to a ChallengeBucket shape so the same table columns render it. */
function challengeToBucket(c: AuthzChallenge): ChallengeBucket {
  return {
    id: c.id,
    lastSeen: c.timestamp,
    firstSeen: c.timestamp,
    organizationId: c.organizationId,
    projectId: c.projectId,
    principalUrn: c.principalUrn,
    principalType: c.principalType,
    userEmail: c.userEmail,
    photoUrl: c.photoUrl,
    operation: c.operation,
    outcome: c.outcome,
    reason: c.reason,
    scope: c.scope,
    resourceKind: c.resourceKind,
    resourceId: c.resourceId,
    roleSlugs: c.roleSlugs,
    evaluatedGrantCount: c.evaluatedGrantCount,
    matchedGrantCount: c.matchedGrantCount,
    challengeCount: 1,
    challengeIds: [c.id],
    resolvedAt: c.resolvedAt,
    resolutionType: c.resolutionType,
    resolvedBy: c.resolvedBy,
    resolutionRoleSlug: c.resolutionRoleSlug,
  };
}

export function OutcomeBadge({
  outcome,
  resolved,
}: {
  outcome: BucketOutcome;
  resolved?: boolean;
}): JSX.Element {
  if (resolved) {
    return (
      <MoonshineBadge variant="neutral">
        <MoonshineBadge.Text>Resolved</MoonshineBadge.Text>
      </MoonshineBadge>
    );
  }

  const config: Record<
    BucketOutcome,
    { variant: "destructive" | "success" | "neutral"; label: string }
  > = {
    deny: { variant: "destructive", label: "Denied" },
    allow: { variant: "success", label: "Allowed" },
    error: { variant: "neutral", label: "Error" },
  };
  const c = enumPresentation(config, outcome, {
    variant: "neutral",
    label: "Unknown",
  });

  return (
    <MoonshineBadge variant={c.variant}>
      <MoonshineBadge.Text>{c.label}</MoonshineBadge.Text>
    </MoonshineBadge>
  );
}

function FilterPill({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1.5 border px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "border-primary bg-primary/5 text-primary"
          : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {children}
      {count !== undefined && (
        <span
          className={cn(
            "tabular-nums",
            active ? "text-primary/70" : "text-muted-foreground/70",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ChallengesEmptyState({
  outcomeFilter,
}: {
  outcomeFilter: OutcomeFilter;
}): JSX.Element {
  return (
    <div className="border-border/50 bg-muted/20 border px-6 py-16 text-center">
      <div className="bg-primary/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
        <Check className="text-primary h-6 w-6" />
      </div>
      <Text variant="body" className="font-medium">
        {outcomeFilter === "deny"
          ? "No denied access attempts"
          : outcomeFilter === "resolved"
            ? "No resolved challenges yet"
            : "No challenges found"}
      </Text>
      <Text variant="body" className="text-muted-foreground mt-1 text-sm">
        {outcomeFilter === "deny"
          ? "All authorization checks are passing. Your team's permissions look good."
          : outcomeFilter === "resolved"
            ? "Denied challenges that are resolved by granting access will appear here."
            : "Authorization challenges will appear here as your team uses the platform."}
      </Text>
    </div>
  );
}

function useOutcomeApiParam(outcomeFilter: OutcomeFilter): {
  outcome?: Outcome;
  resolved?: boolean;
} {
  if (outcomeFilter === "deny")
    return { outcome: Outcome.Deny, resolved: false };
  if (outcomeFilter === "allow") return { outcome: Outcome.Allow };
  if (outcomeFilter === "resolved") return { resolved: true };
  return {};
}

const EXPANDED_PREVIEW_LIMIT = 10;

/** Fetch individual challenges for all currently-expanded buckets (first 10 only). */
function useExpandedChallenges(
  expandedIds: Set<string>,
  buckets: ChallengeBucket[],
): Map<string, ChallengeBucket[]> {
  // Only fetch the first EXPANDED_PREVIEW_LIMIT IDs per bucket.
  const allIds = useMemo(() => {
    const ids: string[] = [];
    for (const b of buckets) {
      if (expandedIds.has(b.id) && b.challengeCount > 1) {
        ids.push(...b.challengeIds.slice(0, EXPANDED_PREVIEW_LIMIT));
      }
    }
    return ids;
  }, [expandedIds, buckets]);

  const { data } = useChallenges(
    allIds.length > 0 ? { ids: allIds } : undefined,
  );

  return useMemo(() => {
    const map = new Map<string, ChallengeBucket[]>();
    if (!data?.challenges) return map;

    const byId = new Map<string, AuthzChallenge>();
    for (const c of data.challenges) {
      byId.set(c.id, c);
    }

    for (const b of buckets) {
      if (!expandedIds.has(b.id) || b.challengeCount <= 1) continue;
      const rows: ChallengeBucket[] = [];
      for (const id of b.challengeIds.slice(0, EXPANDED_PREVIEW_LIMIT)) {
        const c = byId.get(id);
        if (c) rows.push(challengeToBucket(c));
      }
      if (rows.length > 0) map.set(b.id, rows);
    }
    return map;
  }, [data, buckets, expandedIds]);
}

type FlatRow =
  | { kind: "bucket"; bucket: ChallengeBucket }
  | { kind: "child"; bucket: ChallengeBucket }
  | { kind: "overflow"; bucketId: string; remaining: number };

/** Flatten buckets with expanded rows and overflow indicators interleaved. */
function flattenWithExpanded(
  buckets: ChallengeBucket[],
  expandedMap: Map<string, ChallengeBucket[]>,
): FlatRow[] {
  const result: FlatRow[] = [];
  for (const b of buckets) {
    result.push({ kind: "bucket", bucket: b });
    const expanded = expandedMap.get(b.id);
    if (expanded) {
      for (const child of expanded) {
        result.push({ kind: "child", bucket: child });
      }
      const remaining = b.challengeCount - EXPANDED_PREVIEW_LIMIT;
      if (remaining > 0) {
        result.push({ kind: "overflow", bucketId: b.id, remaining });
      }
    }
  }
  return result;
}

export function ChallengesTab(): JSX.Element {
  const [searchParams] = useSearchParams();
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>("deny");
  const [principalFilter, setPrincipalFilter] = useState(
    searchParams.get("identity") ?? "all",
  );

  // Sync principalFilter during render when URL param changes (no stale frame).
  const prevIdentityRef = useRef(searchParams.get("identity"));
  const identity = searchParams.get("identity");
  if (identity !== prevIdentityRef.current) {
    prevIdentityRef.current = identity;
    setPrincipalFilter(identity ?? "all");
  }
  const [scopeFilter, setScopeFilter] = useState("all");

  const {
    actionsColumn,
    grantFlowPortals,
    animatingOutIds,
    recentlyResolvedIds,
  } = useGrantFlow();

  const PAGE_SIZE = 50;
  const [accumulated, setAccumulated] = useState<ChallengeBucket[]>([]);
  const [pageCount, setPageCount] = useState(1);

  const outcomeParam = useOutcomeApiParam(outcomeFilter);
  const offset = (pageCount - 1) * PAGE_SIZE;

  const { data, isLoading, isFetching, isPlaceholderData } =
    useChallengeBuckets(
      {
        ...outcomeParam,
        principalUrn: principalFilter !== "all" ? principalFilter : undefined,
        scope: scopeFilter !== "all" ? scopeFilter : undefined,
        limit: PAGE_SIZE,
        offset,
      },
      undefined,
      { placeholderData: keepPreviousData },
    );

  // Reset accumulated data when filters change.
  const filterKey = `${outcomeFilter}|${principalFilter}|${scopeFilter}`;
  const prevFilterKeyRef = useRef(filterKey);
  if (filterKey !== prevFilterKeyRef.current) {
    prevFilterKeyRef.current = filterKey;
    setAccumulated([]);
    setPageCount(1);
  }

  // Buckets from the page the query currently holds, excluding those with no
  // scope. Placeholder data still belongs to the previous filter, so it is
  // ignored until the new filter's page arrives.
  const currentPageBuckets = useMemo(() => {
    if (isPlaceholderData || !data?.buckets) return [];
    return data.buckets.filter(isDisplayableBucket);
  }, [data, isPlaceholderData]);

  // Accumulate results as pages load.
  useEffect(() => {
    if (isPlaceholderData || !data?.buckets) return;
    const withScope = data.buckets.filter(isDisplayableBucket);
    if (pageCount === 1) {
      setAccumulated(withScope);
    } else {
      setAccumulated((prev) => {
        const existingIds = new Set(prev.map((b) => b.id));
        const newItems = withScope.filter((b) => !existingIds.has(b.id));
        return [...prev, ...newItems];
      });
    }
  }, [data, pageCount, isPlaceholderData]);

  // Derive the visible rows during render rather than reading `accumulated`
  // directly. A filter switch resets `accumulated` during render while the
  // effect above only refills it after paint, so switching to a filter already
  // in the query cache would otherwise commit one frame of the empty state.
  const rows = useMemo(() => {
    if (accumulated.length === 0) return currentPageBuckets;
    const seen = new Set(accumulated.map((b) => b.id));
    return [
      ...accumulated,
      ...currentPageBuckets.filter((b) => !seen.has(b.id)),
    ];
  }, [accumulated, currentPageBuckets]);

  const totalBuckets = data?.total ?? 0;
  const hasMore = rows.length < totalBuckets;
  const isLoadingMore = isFetching && pageCount > 1;

  // Pill counts: fetch totals for each tab (lightweight, limit=1).
  const baseFilters = {
    principalUrn: principalFilter !== "all" ? principalFilter : undefined,
    scope: scopeFilter !== "all" ? scopeFilter : undefined,
    limit: 1,
    offset: 0,
  };
  const { data: denyData } = useChallengeBuckets({
    ...baseFilters,
    outcome: Outcome.Deny,
    resolved: false,
  });
  const { data: resolvedData } = useChallengeBuckets({
    ...baseFilters,
    resolved: true,
  });
  const { data: allData } = useChallengeBuckets(baseFilters);

  // Named agents for the rows and the dropdown. The challenge carries only
  // the urn, and an id off a challenge is not permission to open the agent,
  // so the roster is both the name and the decision to link.
  const agents = useReadableAgents(
    rows.some((bucket) => bucket.principalType === "agent"),
  );
  const agentsById = useMemo(
    () =>
      new Map(
        (agents.isError ? [] : (agents.data ?? [])).map((agent) => [
          agent.id,
          agent,
        ]),
      ),
    [agents.data, agents.isError],
  );

  // Unique values for filter dropdowns (from loaded data).
  const uniquePrincipals = useMemo(() => {
    const principals = new Map<string, string>();
    for (const bucket of rows) {
      // Keyed by what the option actually filters on. The API narrows by
      // `principal_urn`, and an agent challenge can carry the acting user's
      // address — keying by that would have hung an agent's name on a
      // person's filter value, and merged two agents into one option.
      const value =
        bucket.principalType === "agent"
          ? bucket.principalUrn
          : (bucket.userEmail ?? bucket.principalUrn);
      if (!value) continue;
      // An option is plain text, so an agent takes the shared wording rather
      // than the urn the identity helper falls back to. Said in one place, so
      // the dropdown and the rows below it name the same agent the same way.
      if (bucket.principalType === "agent") {
        const id = bucket.principalUrn.replace(/^agent:/, "");
        principals.set(
          value,
          actingPrincipalText({ id, name: agentsById.get(id)?.name }),
        );
      } else {
        principals.set(
          value,
          principalDisplayName(bucket.userEmail, bucket.principalUrn),
        );
      }
    }
    return [...principals.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows, agentsById]);

  const uniqueScopes = useMemo(() => {
    const set = new Set(rows.map((b) => b.scope));
    return [...set].filter(Boolean).sort();
  }, [rows]);

  // Expansion state.
  const [expandedBucketIds, setExpandedBucketIds] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleBucket = useCallback((bucketId: string) => {
    setExpandedBucketIds((prev) => {
      const next = new Set(prev);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });
  }, []);

  const expandedMap = useExpandedChallenges(expandedBucketIds, rows);
  const flatData = useMemo(
    () => flattenWithExpanded(rows, expandedMap),
    [rows, expandedMap],
  );

  // IDs of individual challenge rows (expanded children) for styling and column hiding.
  // Exclude bucket trigger row IDs so they keep their avatar/identity/outcome.
  const expandedChildIds = useMemo(() => {
    const bucketIds = new Set(rows.map((b) => b.id));
    const ids = new Set<string>();
    for (const childRows of expandedMap.values()) {
      for (const r of childRows) {
        if (!bucketIds.has(r.id)) ids.add(r.id);
      }
    }
    return ids;
  }, [expandedMap, rows]);

  const challengeRowColumns = useChallengeRowColumns(
    animatingOutIds,
    outcomeFilter,
    toggleBucket,
    expandedChildIds,
    recentlyResolvedIds,
    agentsById,
  );

  const wrappedActionsColumn: Column<ChallengeBucket> = useMemo(
    () => ({
      ...actionsColumn,
      render: (row: ChallengeBucket) =>
        expandedChildIds.has(row.id) ? null : actionsColumn.render?.(row),
    }),
    [actionsColumn, expandedChildIds],
  );

  const columns: Column<ChallengeBucket>[] = [
    ...challengeRowColumns,
    wrappedActionsColumn,
  ];

  // Show the skeleton for the first/active load of the current filter, so a slow
  // load never flashes the "no challenges" empty state. Two cases need it:
  //   - initial load: `isLoading` is true (no data yet); and
  //   - a filter switch under `keepPreviousData`: `isLoading` stays false because
  //     the previous filter's data lingers, but `isPlaceholderData` is true while
  //     the new filter's request is in flight (so there is nothing to show yet).
  // Gating on these (rather than the raw `isFetching`) keeps the empty state
  // stable during background refetches of a filter that genuinely has no results.
  const hasRows = rows.length > 0;
  const showInitialLoading = !hasRows && (isLoading || isPlaceholderData);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterPill
          active={outcomeFilter === "deny"}
          onClick={() => setOutcomeFilter("deny")}
          count={denyData?.total}
        >
          Denied
        </FilterPill>
        <FilterPill
          active={outcomeFilter === "resolved"}
          onClick={() => setOutcomeFilter("resolved")}
          count={resolvedData?.total}
        >
          Resolved
        </FilterPill>
        <FilterPill
          active={outcomeFilter === "all"}
          onClick={() => setOutcomeFilter("all")}
          count={allData?.total}
        >
          All
        </FilterPill>

        <div className="border-border mx-1 h-6 border-l" />

        <Select value={principalFilter} onValueChange={setPrincipalFilter}>
          <SelectTrigger
            size="sm"
            className="h-8 w-auto max-w-[280px] min-w-fit"
          >
            <SelectValue>
              {principalFilterLabel(
                principalFilter,
                uniquePrincipals.find(
                  ([value]) => value === principalFilter,
                )?.[1],
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            {uniquePrincipals.map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={scopeFilter} onValueChange={setScopeFilter}>
          <SelectTrigger size="sm" className="h-8 w-[180px]">
            <SelectValue placeholder="All scopes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            {uniqueScopes.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showInitialLoading ? (
        <SkeletonTable />
      ) : !hasRows ? (
        <ChallengesEmptyState outcomeFilter={outcomeFilter} />
      ) : (
        <>
          <Table columns={columns}>
            <Table.Header columns={columns} />
            <Table.Body>
              {flatData.map((row) => {
                if (row.kind === "overflow") {
                  return (
                    <tr
                      key={`overflow-${row.bucketId}`}
                      className="bg-muted/50"
                    >
                      <td
                        colSpan={columns.length}
                        className="text-muted-foreground px-4 py-2 text-center text-xs"
                      >
                        + {row.remaining.toLocaleString()} more
                      </td>
                    </tr>
                  );
                }
                return (
                  <Table.Row
                    key={row.bucket.id}
                    row={row.bucket}
                    columns={columns}
                    className={row.kind === "child" ? "bg-muted/50" : undefined}
                  />
                );
              })}
            </Table.Body>
          </Table>
          {(hasRows || isLoadingMore) && (
            <div className="bg-muted/20 flex items-center justify-between border-t px-4 py-3">
              <Text muted small>
                Showing {rows.length.toLocaleString()} of{" "}
                {totalBuckets.toLocaleString()}
              </Text>
              {hasMore ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPageCount((p) => p + 1)}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load more"
                  )}
                </Button>
              ) : (
                <Text muted small>
                  All results loaded
                </Text>
              )}
            </div>
          )}
        </>
      )}

      <div className="border-border/50 bg-muted/30 mt-8 border px-4 py-3">
        <Text variant="subheading" className="mb-3">
          About Challenges
        </Text>
        <div className="space-y-2 text-sm">
          <div className="flex items-start gap-3">
            <MoonshineBadge variant="destructive" className="mt-0.5 shrink-0">
              <MoonshineBadge.Text>Denied</MoonshineBadge.Text>
            </MoonshineBadge>
            <Text variant="body" className="text-muted-foreground text-sm">
              The principal lacked the required scope or grants to perform the
              action. Check role assignments and grant selectors.
            </Text>
          </div>
          <div className="flex items-start gap-3">
            <MoonshineBadge variant="success" className="mt-0.5 shrink-0">
              <MoonshineBadge.Text>Allowed</MoonshineBadge.Text>
            </MoonshineBadge>
            <Text variant="body" className="text-muted-foreground text-sm">
              The principal had matching grants satisfying the requested scope.
            </Text>
          </div>
          <div className="flex items-start gap-3">
            <MoonshineBadge variant="neutral" className="mt-0.5 shrink-0">
              <MoonshineBadge.Text>Resolved</MoonshineBadge.Text>
            </MoonshineBadge>
            <Text variant="body" className="text-muted-foreground text-sm">
              A denied challenge that has since been addressed by granting the
              required access.
            </Text>
          </div>
        </div>
      </div>

      {grantFlowPortals}
    </div>
  );
}
